import * as React from "react";

import { cn } from "@/utils/utils";
import { LucideIcon } from "lucide-react";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startIcon?: LucideIcon | React.ReactNode;
  endIcon?: React.ReactNode;
  containerClassName?: string;
  error?: boolean;
  variant?: "default" | "dark";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      type,
      startIcon,
      endIcon,
      error,
      variant = "default",
      ...props
    },
    ref,
  ) => {
    const StartIcon = startIcon as LucideIcon;
    const isReactNode = React.isValidElement(startIcon);

    return (
      <div className={cn("relative w-full", containerClassName)}>
        {StartIcon && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 transform">
            {isReactNode ? startIcon : <StartIcon size={14} />}
          </div>
        )}
        <input
          type={type}
          className={cn(
            "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm shadow-[0px_4px_4px_-2px_rgba(37,37,39,0.04)] transition-colors disabled:cursor-not-allowed disabled:opacity-50",
            "file:-ml-[6px] file:mt-[3px] file:cursor-pointer file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground",
            "hover:border-neutral-300",
            "focus-visible:border-neutral-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-input",
            type === "file" && "cursor-pointer",
            startIcon ? "pl-8" : "",
            endIcon ? "pr-8" : "",
            variant === "dark" &&
              "border-white/15 !bg-transparent text-white placeholder:text-white/40 focus-visible:border-white/30 focus-visible:ring-0",
            className,
            error && "border-destructive focus-visible:ring-destructive/50",
          )}
          ref={ref}
          {...props}
        />
        {endIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
            {endIcon}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
